import React, { useCallback, useRef, useState } from 'react';
import { ModalOverlay } from './ui';
import eventService from '../services/eventService';
import type { NotifyCount, UpdateEventRequest } from '../types';

/** `true` = mit Mail speichern, `false` = ohne, `null` = Vorgang abgebrochen. */
export type NotifyChoice = boolean | null;

interface NotifyFavoritesDialogProps {
  /** Anzahl der Nutzer, die eine Mail bekämen. Immer >= 1 — sonst wird der Dialog nicht gezeigt. */
  count: number;
  /** Beschriftung der Bestätigen-Aktion, z. B. „Speichern" oder „Zusammenführen". */
  actionLabel: string;
  onChoose: (choice: NotifyChoice) => void;
  /**
   * Zusatzsatz über den Knöpfen — für Umstände, die für eine Mail sprechen. Bewusst nur ein
   * Hinweis und keine Vorauswahl: ein Massenversand ist der teuerste aller Fehler, die letzte
   * Entscheidung bleibt beim Menschen.
   */
  hint?: string;
  /**
   * Heute ging zu diesem Event schon eine Änderungs-Mail raus. Der Server verschickt keine
   * zweite am selben Tag, also gibt es hier nichts zu entscheiden: der Dialog sagt das und
   * bietet nur noch Speichern ohne Mail an.
   */
  alreadyNotifiedToday?: boolean;
}

/**
 * Fragt beim Speichern eines favorisierten Events, ob die Favoriten-Besitzer per Mail über die
 * Änderung informiert werden sollen. Bewusst zwei gleichwertige Buttons statt einer Checkbox:
 * ein Massenversand soll eine getroffene Entscheidung sein, kein übersehenes Häkchen.
 */
const NotifyFavoritesDialog: React.FC<NotifyFavoritesDialogProps> = ({ count, actionLabel, onChoose, hint, alreadyNotifiedToday }) => (
  <ModalOverlay
    role="alertdialog"
    ariaLabelledBy="notify-favorites-title"
    ariaDescribedBy="notify-favorites-desc"
    onKeyDown={(e) => { if (e.key === 'Escape') onChoose(null); }}
  >
    <div className="rounded-lg p-6 w-full max-w-md" style={{ background: 'var(--surface-bg)' }}>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--badge-amber-bg)' }}
        >
          <svg
            className="h-5 w-5"
            style={{ color: 'var(--badge-amber-text)' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
            />
          </svg>
        </div>
        <div>
          <h2 id="notify-favorites-title" className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {alreadyNotifiedToday ? 'Heute ging schon eine Mail raus' : 'Nutzer benachrichtigen?'}
          </h2>
          <p id="notify-favorites-desc" className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            {alreadyNotifiedToday
              ? `${count === 1 ? '1 Nutzer hat' : `${count} Nutzer haben`} dieses Event favorisiert und heute bereits eine Änderungs-Mail bekommen. Eine zweite geht am selben Tag nicht raus. Die Event-Seite hinter dem Link zeigt ohnehin den aktuellen Stand.`
              : count === 1
                ? '1 Nutzer hat dieses Event favorisiert und bekommt eine E-Mail mit den Änderungen.'
                : `${count} Nutzer haben dieses Event favorisiert und bekommen eine E-Mail mit den Änderungen.`}
          </p>
          {!alreadyNotifiedToday && hint && (
            <p className="text-sm mt-2" style={{ color: 'var(--text-primary)' }}>{hint}</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <button
          onClick={() => onChoose(null)}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{ color: 'var(--text-secondary)', background: 'var(--table-header-bg)' }}
        >
          Abbrechen
        </button>
        {alreadyNotifiedToday ? (
          <button
            onClick={() => onChoose(false)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors bg-brand-600 hover:bg-brand-700"
            autoFocus
          >
            {actionLabel} ohne Mail
          </button>
        ) : (
          <>
            <button
              onClick={() => onChoose(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ color: 'var(--text-primary)', background: 'var(--table-header-bg)' }}
            >
              Ohne Benachrichtigung
            </button>
            <button
              onClick={() => onChoose(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors bg-brand-600 hover:bg-brand-700"
              autoFocus
            >
              {actionLabel} &amp; benachrichtigen
            </button>
          </>
        )}
      </div>
    </div>
  </ModalOverlay>
);

/**
 * Kapselt den Ablauf „zählen → ggf. fragen → Antwort zurückgeben" für alle Stellen, an denen ein
 * bestehendes Event verändert wird (Admin-Formular, Event-Detailseite, Vorschlag genehmigen,
 * Zusammenführen).
 *
 * Aufruf im Speichern-Handler:
 * ```ts
 * const choice = await confirmNotifyUpdate(event.id, data);
 * if (choice === null) return;              // Nutzer hat abgebrochen
 * await eventService.update(id, data, choice);
 * ```
 * und `{notifyDialog}` einmal ins JSX hängen.
 *
 * `confirmNotifyUpdate` fragt den Server vorab, ob genau dieser Formularstand eine Mail auslösen
 * würde, und fragt nur dann — eine neue Preisstufe allein etwa nie. `confirmNotify` zählt nur
 * die Favoriten und bleibt für Genehmigen und Zusammenführen, die ihre Änderung erst im Server
 * zusammensetzen.
 */
export function useNotifyFavoritesConfirm(actionLabel = 'Speichern') {
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [hint, setHint] = useState<string | undefined>(undefined);
  const [alreadyToday, setAlreadyToday] = useState(false);
  const resolverRef = useRef<((choice: NotifyChoice) => void) | null>(null);

  const ask = useCallback(async (load: () => Promise<NotifyCount>, hintText?: string): Promise<NotifyChoice> => {
    let count = 0;
    let alreadyNotifiedToday = false;
    try {
      ({ count, alreadyNotifiedToday } = await load());
    } catch {
      // Zählung fehlgeschlagen: lieber ohne Mail speichern als das Speichern blockieren.
      return false;
    }
    if (count < 1) return false;
    return new Promise<NotifyChoice>((resolve) => {
      resolverRef.current = resolve;
      setHint(hintText);
      setAlreadyToday(alreadyNotifiedToday);
      setPendingCount(count);
    });
  }, []);

  const confirmNotify = useCallback((
    eventIds: (number | undefined | null)[],
    hintText?: string,
  ): Promise<NotifyChoice> => {
    const ids = eventIds.filter((id): id is number => typeof id === 'number');
    return ask(() => eventService.getNotifyCount(ids), hintText);
  }, [ask]);

  const confirmNotifyUpdate = useCallback((eventId: number, data: UpdateEventRequest): Promise<NotifyChoice> =>
    ask(() => eventService.getNotifyPreview(eventId, data)), [ask]);

  const settle = useCallback((choice: NotifyChoice) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPendingCount(null);
    resolve?.(choice);
  }, []);

  const notifyDialog = pendingCount !== null
    ? <NotifyFavoritesDialog count={pendingCount} actionLabel={actionLabel} onChoose={settle} hint={hint} alreadyNotifiedToday={alreadyToday} />
    : null;

  return { confirmNotify, confirmNotifyUpdate, notifyDialog };
}

export default NotifyFavoritesDialog;
